import { DummyAuthProvider, RopcAuthProvider, type RopcConfig } from '@fluxora/auth';
import { I18nProvider } from '@fluxora/i18n';
import { ThemeProvider } from '@fluxora/theme';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider } from 'react-router-dom';


import { country } from './config/country';
import { env } from './config/env';
import { DUMMY_ORG, DUMMY_USERS } from './fixtures/dummyUsers';
import { AuthStoreBridge } from './lib/authStore';
import { OrgProvider } from './lib/orgContext';
import { createQueryClient } from './lib/queryClient';
import { router } from './router';
import { ThemeTweaksBridge } from './theme/tweaks/ThemeTweaksBridge';

const queryClient = createQueryClient();

// Hard fail-fast: refuse to ship the DummyAuthProvider in a PRODUCTION build.
// Slice 2: this guard now ONLY fires for the dangerous combination
// (devMode=true AND a production build) — the real-provider production path
// (devMode=false) must NOT be blocked. Dev builds with devMode=true are fine;
// prod builds with devMode=false are fine. Only `devMode && PROD` throws.
function assertDevAuthAllowed(): void {
  if (env.devMode && import.meta.env.PROD) {
    throw new Error(
      'DummyAuthProvider is dev-only and was reached in a PRODUCTION build. ' +
        'Set VITE_DEV_MODE=false for production so the real OIDC AuthProvider renders. ' +
        'This guard backs the Phase 6 bundle-grep gate; we do not rely on CI alone.',
    );
  }
}

/** Build the ROPC provider config from validated env — the backend login URL. */
function buildRopcConfig(): RopcConfig {
  const loginUrl = `${env.accountsApiUrl}/v1/auth/login`;
  // TEST KNOB — a short, non-renewing session so the expiry path can be walked
  // by hand. `import.meta.env.DEV` is statically false in a production build,
  // so this branch is dropped from the bundle entirely and the env var cannot
  // shorten anyone's real session. See `RopcConfig.testSessionSeconds`.
  const testSeconds = env.testSessionSeconds;
  if (import.meta.env.DEV && Number.isFinite(testSeconds) && testSeconds > 0) {
    return { loginUrl, testSessionSeconds: testSeconds };
  }
  return { loginUrl };
}

/**
 * The provider-wrapped app body. Identical inner tree under both providers —
 * AuthStoreBridge + RouterProvider + TweaksTrigger all read the shared
 * AuthContext, so they are provider-agnostic.
 */
function AppInner(): JSX.Element {
  return (
    <>
      <AuthStoreBridge />
      {/* Applies the saved theme on load. Without it the theme was only ever
          applied by the Settings screen. */}
      <ThemeTweaksBridge />
      <RouterProvider router={router} />
    </>
  );
}

export function App(): JSX.Element {
  assertDevAuthAllowed();
  const inner = <AppInner />;
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider country={country.code} defaultTheme="light">
        <I18nProvider locale={country.locale}>
          {/* OrgProvider resolves the subdomain slug → tenant org at boot.
              It is intentionally outside the auth providers so it runs
              before login — the result is PRE-LOGIN branding only. */}
          <OrgProvider>
            {env.devMode ? (
              // Dev path: in-memory fixtures, no Keycloak.
              <DummyAuthProvider users={DUMMY_USERS} organization={DUMMY_ORG}>
                {inner}
              </DummyAuthProvider>
            ) : (
              // Real path: the CRM's own login form → backend POST /v1/auth/login
              // → Keycloak password grant (ROPC). No redirect, no oidc-client-ts.
              <RopcAuthProvider config={buildRopcConfig()}>{inner}</RopcAuthProvider>
            )}
          </OrgProvider>
        </I18nProvider>
      </ThemeProvider>
      {/* `buttonPosition` moved off the default bottom-right: that corner is the
          Rheora AI launcher's (see `CrmAssistantMount`). The devtools wrapper is
          a fixed container with a very high z-index, so it painted OVER the
          launcher's z-1250 and made the assistant look missing in dev — while
          in a production build, where the devtools do not render at all, the
          launcher was fine. A dev-only tool yields to the product surface
          rather than the other way round. */}
      {import.meta.env.DEV && (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      )}
    </QueryClientProvider>
  );
}
